import { S3Client } from '@aws-sdk/client-s3'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import type { Media, ImageOutputs, VideoOutputs } from './utils/media'
export const uuid = (length: number) => {
  const characters = '0123456789'
  let result = ''
  const charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charactersLength)
    result += characters.charAt(randomIndex)
  }
  return result
}
export const getThumbnailUrl = (media: Media) => {
  if (media.mediaType === 'image') {
    return (
      (media.outputs as ImageOutputs)?.thumbnail?.cdnUrl ||
      (media.outputs as ImageOutputs)?.thumbnail?.s3Uri ||
      media.original.cdnUrl ||
      ''
    )
  }
  return (
    media.thumbnail?.cdnUrl ||
    media.thumbnail?.url ||
    media.original.cdnUrl ||
    media.original.s3Uri ||
    ''
  )
}
// Show/hide Cesium animation and timeline controls
export const showTimelineControls = () => {
  const animationContainer = document.querySelector<HTMLElement>(
    '.cesium-viewer-animationContainer',
  )
  if (animationContainer) {
    animationContainer.style.display = 'block'
  }
  const timelineContainer = document.querySelector<HTMLElement>('.cesium-viewer-timelineContainer')
  if (timelineContainer) {
    timelineContainer.style.display = 'block'
  }
}

export const hideTimelineControls = () => {
  const animationContainer = document.querySelector<HTMLElement>(
    '.cesium-viewer-animationContainer',
  )
  if (animationContainer) {
    animationContainer.style.display = 'none'
  }
  const timelineContainer = document.querySelector<HTMLElement>('.cesium-viewer-timelineContainer')
  if (timelineContainer) {
    timelineContainer.style.display = 'none'
  }
}
/**
 *
 * @param timestamp - The timestamp to check
 * @param selectedDateValue - The selected date to compare to
 * @returns True if the timestamp matches the selected date, false otherwise
 * @example isDateMatch(1704067200, '2024-01-01') → true
 */
export const isDateMatch = (
  timestamp: number,
  selectedDateValue: string | Date | null,
): boolean => {
  if (!selectedDateValue) return true

  const mediaDate = new Date(timestamp * 1000)
  let selectedDateObj: Date

  if (selectedDateValue instanceof Date) {
    selectedDateObj = selectedDateValue
  } else if (typeof selectedDateValue === 'string') {
    selectedDateObj = new Date(selectedDateValue)
  } else {
    return true
  }

  // Compare year, month, and day only
  return (
    mediaDate.getFullYear() === selectedDateObj.getFullYear() &&
    mediaDate.getMonth() === selectedDateObj.getMonth() &&
    mediaDate.getDate() === selectedDateObj.getDate()
  )
}
/**
 * Check if the media type matches the filter value
 * @param mediaType - The media type to check
 * @param filterValue - The filter value to compare to
 * @returns True if the media type matches the filter value, false otherwise
 * @example isMediaTypeMatch('image', 'Images') → true
 * @example isMediaTypeMatch('video', 'Videos') → true
 * @example isMediaTypeMatch('image', 'Videos') → false
 */
export const isMediaTypeMatch = (mediaType: 'image' | 'video', filterValue: string): boolean => {
  if (!filterValue) return true
  if (filterValue === 'Images') return mediaType === 'image'
  if (filterValue === 'Videos') return mediaType === 'video'
  return true
}
/*
 * Date formatters are constructed once at module scope rather than per call.
 * `toLocaleDateString` builds a fresh Intl formatter every time, and these run
 * once per tile — on a 4,900-item gallery that was thousands of formatter
 * constructions per render pass.
 */
const _dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

/**
 * Format a timestamp to a date string
 * @example formatDate(1704067200) → "Jan 1, 2024"
 */
export const formatDate = (timestamp?: number) => {
  if (!timestamp) return 'Unknown date'
  return _dateFmt.format(new Date(timestamp * 1000))
}

const _utcDayFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})
const _utcMonthFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
})
const _utcTimeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})
const _utcDateShortFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

/**
 * Real capture time in epoch seconds, falling back to ingest time for rows the
 * capturedAt backfill has not reached. Always read capture time through this.
 */
export const getCapturedAt = (media: Media): number => media.capturedAt || media.createdAt || 0

/** @example getUtcDayKey(1786099895) → "2026-08-07" */
export const getUtcDayKey = (seconds: number): string =>
  new Date(seconds * 1000).toISOString().slice(0, 10)

/** @example getUtcMonthKey(1786099895) → "2026-08" */
export const getUtcMonthKey = (seconds: number): string =>
  new Date(seconds * 1000).toISOString().slice(0, 7)

/** @example getUtcYearKey(1786099895) → "2026" */
export const getUtcYearKey = (seconds: number): string =>
  new Date(seconds * 1000).toISOString().slice(0, 4)

/** @example formatUtcDayLabel(1786099895) → "Fri, 07 Aug 2026" */
export const formatUtcDayLabel = (seconds: number): string =>
  _utcDayFmt.format(new Date(seconds * 1000))

/** @example formatUtcMonthLabel(1786099895) → "August 2026" */
export const formatUtcMonthLabel = (seconds: number): string =>
  _utcMonthFmt.format(new Date(seconds * 1000))

/** @example formatUtcYearLabel(1786099895) → "2026" */
export const formatUtcYearLabel = (seconds: number): string => getUtcYearKey(seconds)

/** Time of day only — used on tiles where the day is already in the header. */
export const formatCapturedTime = (seconds?: number): string =>
  seconds ? _utcTimeFmt.format(new Date(seconds * 1000)) : 'Unknown time'

/** @example formatCapturedDateShort(1786099895) → "07 Aug 2026" */
export const formatCapturedDateShort = (seconds?: number): string =>
  seconds ? _utcDateShortFmt.format(new Date(seconds * 1000)) : 'Unknown date'

/** @example formatCapturedDateTime(1786099895) → "07 Aug 2026, 10:51" */
export const formatCapturedDateTime = (seconds?: number): string =>
  seconds ? `${formatCapturedDateShort(seconds)}, ${formatCapturedTime(seconds)}` : 'Unknown date'

/**
 * Matches a capture time against the picked day, for the Files tab.
 *
 * Compares on the device's own calendar day, which is what the day-section
 * headers show and what a pilot means by "the day I flew this".
 *
 * The Folders tab deliberately keeps `isDateMatch`: `folder.createdAt` is a
 * genuine server timestamp, so comparing it in the viewer's local time is
 * correct there.
 */
export const isUtcDateMatch = (
  seconds: number,
  selectedDateValue: string | Date | null,
): boolean => {
  if (!selectedDateValue) return true

  const selected =
    selectedDateValue instanceof Date ? selectedDateValue : new Date(selectedDateValue)
  if (Number.isNaN(selected.getTime())) return true

  // DatePicker emits a local YYYY-MM-DD, so read the day the user picked off
  // its local parts rather than round-tripping it through UTC.
  const picked =
    typeof selectedDateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(selectedDateValue)
      ? selectedDateValue.slice(0, 10)
      : `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, '0')}-${String(
          selected.getDate(),
        ).padStart(2, '0')}`

  return getUtcDayKey(seconds) === picked
}

/**
 * Empty-state copy for the media gallery, shared by Files and Folders.
 *
 * Lives here because it existed in both components and the Files copy had
 * drifted: it treated the 'All' media-type filter as an active filter.
 */
export const buildMediaEmptyStateMessage = (opts: {
  selectedDate?: string | Date | null
  mediaTypeFilter?: string
  searchQuery?: string
  hasMore?: boolean
  emptyMessage?: string
}): string => {
  const parts: string[] = []

  if (opts.selectedDate) {
    const dateObj =
      opts.selectedDate instanceof Date ? opts.selectedDate : new Date(opts.selectedDate)
    parts.push(
      `on ${dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`,
    )
  }
  if (opts.mediaTypeFilter && opts.mediaTypeFilter !== 'All') {
    parts.push(`for ${opts.mediaTypeFilter.toLowerCase()}`)
  }
  if (opts.searchQuery) {
    parts.push(`matching "${opts.searchQuery}"`)
  }

  if (!parts.length) {
    return (
      opts.emptyMessage ??
      'You have no files yet for this device, files will appear here once they are uploaded.'
    )
  }

  // Filtering runs over what has been loaded so far, so an empty result with
  // more pages available is not the same as "there is none".
  if (opts.hasMore) {
    return `No media ${parts.join(' ')} in the items loaded so far. Keep scrolling to load more.`
  }
  return `There are no media ${parts.join(' ')}.`
}
/**
 * Display name for a media item: the user-assigned name if it has been renamed
 * (FLY3D-1037), otherwise the filename derived from the S3 key path.
 *
 * This is the single derivation point — tile labels, the viewer title, the
 * archive list, search filtering and download filenames all read through it, so
 * a rename propagates everywhere from here. Do not re-derive the name elsewhere.
 */
export const getFileName = (media: Media): string => {
  return media.originalFileName || media.original?.s3Uri?.split('/').pop() || ''
}
/**
 * Truncate a string to 8 characters and add ... to the end
 * @example truncate("my_video.mp4") → "my_video..."
 */
export const truncate = (id: string) => {
  if (id.length <= 20) return id
  return `${id.slice(0, 20)}...${id.slice(-4)}`
}
export const formatFileSize = (bytes?: number) => {
  if (bytes === 0) return '0 B'
  if (!bytes) return 'Unknown size'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
}
export const generateUUID = () => {
  const characters = '0123456789abcdef'
  const segments = [8, 4, 4, 4, 12]
  const uuid = segments.map((segmentLength) => {
    let segment = ''
    for (let i = 0; i < segmentLength; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length)
      segment += characters.charAt(randomIndex)
    }
    return segment
  })
  return uuid.join('-')
}

export const s3Client = new S3Client({
  region: 'eu-west-2',
  credentials: fromCognitoIdentityPool({
    clientConfig: { region: 'eu-west-2' },
    identityPoolId: 'eu-west-2:75ce4c20-7472-4a6d-bcfb-9ff4489ce8bf',
  }),
})

const stringToHash = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = hash | 0
  }
  return Math.abs(hash)
}

export const encodeUserId = (uniqueString: string) => {
  const hash = stringToHash(uniqueString)
  const number = hash % 10000000000
  return String(number).padStart(10, '0')
}
export const getRCName = (rcName: string | undefined): string => {
  if (!rcName) return 'None'
  return rcName.replace(/_/g, ' ')
}
export const getLinkStateText = (state: number | undefined): string => {
  if (state === 1) return 'Connected'
  return 'Disconnected'
}
export const getLinkWorkmodeText = (mode: number | undefined): string => {
  return mode === 1 ? '4G Fusion Mode' : 'SDR Mode'
}
export const getSignalStatus = (signal: number | undefined): string => {
  if (!signal) return '0 Bad'
  if (signal < 40) return `${signal} Bad`
  if (signal <= 60) return `${signal} Normal`
  return `${signal} Good`
}
